/**
 * Manually trigger a service-worker update check.
 *
 * The automatic detection (main.tsx) runs at visibilitychange + every 15 min
 * — this is the user-initiated equivalent, surfaced via a menu button so a
 * vendor can confirm they're on the latest version without waiting.
 *
 * Returns:
 *  - "updating" if a new SW was found (auto-reload will fire via the
 *    `controlling` listener in main.tsx)
 *  - "up-to-date" if no update within 5s (considered current)
 *  - "no-sw" if the browser supports SW but none is registered (dev mode:
 *    main.tsx only registers in `import.meta.env.PROD`)
 *  - "unsupported" if the browser has no SW API at all
 */
export type UpdateCheckResult = 'updating' | 'up-to-date' | 'no-sw' | 'unsupported';

export async function checkForUpdates(): Promise<UpdateCheckResult> {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) {
    return 'unsupported';
  }
  const registration = await navigator.serviceWorker.getRegistration();
  if (!registration) return 'no-sw';

  return new Promise((resolve) => {
    let settled = false;
    const finish = (r: UpdateCheckResult) => {
      if (settled) return;
      settled = true;
      registration.removeEventListener('updatefound', onUpdateFound);
      window.clearTimeout(timeout);
      resolve(r);
    };
    const onUpdateFound = () => finish('updating');
    registration.addEventListener('updatefound', onUpdateFound);
    const timeout = window.setTimeout(() => finish('up-to-date'), 5000);
    void registration.update();
  });
}
